import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import * as bookingApi from '@/services/bookingApi';
import type { Booking, BookingStatus } from '@/types';

interface BookingDrawerProps {
  booking: Booking | null;
  onClose: () => void;
  onUpdated: () => void;
}

const TERMINAL_STATUSES: BookingStatus[] = [
  'completed',
  'cancelled',
  'rejected',
  'no_show',
];

function formatTime(booking: Booking): string {
  if (!booking.booking_time) return '';
  const [hours, minutes] = booking.booking_time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

export function BookingDrawer({ booking, onClose, onUpdated }: BookingDrawerProps) {
  const [details, setDetails] = useState<Booking | null>(booking);
  const [internalNotes, setInternalNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!booking) {
      setDetails(null);
      return;
    }

    setLoading(true);
    setError(null);
    bookingApi
      .getProviderBooking(booking.id)
      .then((data) => {
        setDetails(data);
        setInternalNotes(data.internal_notes || '');
      })
      .catch(() => {
        setDetails(booking);
        setInternalNotes(booking.internal_notes || '');
      })
      .finally(() => setLoading(false));
  }, [booking]);

  if (!booking) return null;

  const current = details ?? booking;
  const isTerminal = TERMINAL_STATUSES.includes(current.status);
  const canConfirm =
    current.status === 'pending_verification' || current.status === 'pending_confirmation';
  const canComplete = current.status === 'confirmed';
  const canCancel = !isTerminal;

  const runAction = async (action: () => Promise<Booking>) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await action();
      setDetails(updated);
      onUpdated();
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Action failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    setError(null);
    try {
      const updated = await bookingApi.updateInternalNotes(current.id, internalNotes);
      setDetails(updated);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to save notes'
      );
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-card border-l shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-foreground">Booking details</h2>
          <button type="button" onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {loading && !details ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <section>
                <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">Customer</h3>
                <p className="font-medium text-foreground">{current.customer_name}</p>
                <p className="text-sm text-muted-foreground">{current.customer_email}</p>
                {current.customer_phone && (
                  <p className="text-sm text-muted-foreground">{current.customer_phone}</p>
                )}
              </section>

              <section>
                <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">Appointment</h3>
                <p className="text-foreground">
                  {current.booking_date} at {formatTime(current)}
                </p>
                <p className="text-sm text-muted-foreground">{current.duration_minutes} min</p>
                <p className="text-sm mt-1 capitalize">{current.status.replace(/_/g, ' ')}</p>
              </section>

              <section>
                <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">Services</h3>
                <ul className="space-y-1 text-sm text-foreground">
                  {current.services?.map((service) => (
                    <li key={service.id}>
                      {service.name}
                      {service.price_at_booking != null && service.price_at_booking > 0 && (
                        <span className="text-muted-foreground">
                          {' '}
                          · {Number(service.price_at_booking).toFixed(2)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>

              {current.customer_notes && (
                <section>
                  <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">
                    Customer notes
                  </h3>
                  <p className="text-sm text-foreground">{current.customer_notes}</p>
                </section>
              )}

              {current.images && current.images.length > 0 && (
                <section>
                  <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">
                    Reference images
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {current.images.map((image) => (
                      <img
                        key={image.id}
                        src={image.url || image.path}
                        alt={image.original_filename || 'Reference'}
                        className="rounded-lg border object-cover aspect-square w-full"
                      />
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">
                  Internal notes
                </h3>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                  placeholder="Private notes for your team..."
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={saveNotes}
                  disabled={savingNotes}
                >
                  {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save notes'}
                </Button>
              </section>
            </>
          )}
        </div>

        <div className="p-4 border-t bg-muted/30 flex flex-wrap gap-2 justify-end">
          {canCancel && (
            <Button
              variant="outline"
              disabled={loading}
              onClick={() =>
                runAction(() =>
                  bookingApi.cancelProviderBooking(current.id, 'Cancelled by provider')
                )
              }
            >
              Cancel
            </Button>
          )}
          {canConfirm && (
            <>
              <Button
                variant="outline"
                disabled={loading}
                onClick={() => runAction(() => bookingApi.rejectBooking(current.id))}
              >
                Reject
              </Button>
              <Button
                disabled={loading}
                onClick={() => runAction(() => bookingApi.confirmBooking(current.id))}
              >
                Confirm
              </Button>
            </>
          )}
          {canComplete && (
            <>
              <Button
                variant="outline"
                disabled={loading}
                onClick={() => runAction(() => bookingApi.markNoShow(current.id))}
              >
                No-show
              </Button>
              <Button
                disabled={loading}
                onClick={() => runAction(() => bookingApi.completeBooking(current.id))}
              >
                Complete
              </Button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
